import * as THREE from 'three'
import Model from './Model'
import Part from './Part'
import Variant from './Variant'
import Section from './Section'
import Stage from 'components/Designer/Stage'
import { quiltCanvasMaker } from 'components/Designer/MaterialEditor/Quilt/QuiltViewer'
import quiltType from 'components/Designer/MaterialEditor/Quilt/quiltType'
import Material from '../Material/material'
import Quilt, { QuiltElement } from 'designer/geoHandlers/Material/quilt'
import Perforation from 'designer/geoHandlers/Material/perforation'
import colors from 'components/Designer/EditorPopup/predefinedColors'

import Swatch, { SwatchLibrary } from '../Material/swatch'

const SLib = SwatchLibrary.instance
SLib.initialize = colors
const indexed_swatches: Swatch[] = SLib.indexedSwatches

class Island extends THREE.Group {
  uuid: string
  component: string
  section: Section
  variant: Variant
  part: Part
  model: Model
  private _designable: boolean = false
  private _material: Material
  private _swatch: Swatch
  private _quilt: QuiltElement
  private _perforation: Perforation
  private _canvasColor: HTMLCanvasElement | undefined
  private _canvasBump: HTMLCanvasElement | undefined
  private _canvasSpecular: HTMLCanvasElement | undefined
  private _textureColor: THREE.CanvasTexture | null = null
  private _textureBump: THREE.CanvasTexture | null = null
  private _textureSpecular: THREE.CanvasTexture | null = null
  private _debounce_refresh: NodeJS.Timeout | undefined

  private generateCanvas(): HTMLCanvasElement {
    const canv = document.createElement('CANVAS') as HTMLCanvasElement
    canv.width = canv.height = this.resolution
    return canv
  }

  private get resolution(): number {
    if (this.quilt) {
      return this.quilt.resolution
    } else if (this.perforation) {
      return 64
    } else {
      return 64
    }
  }

  constructor({
    uuid,
    model,
    part,
    variant,
    section,
    datatype,
    data,
  }: {
    uuid: string
    model: Model
    part: Part
    variant: Variant
    section: Section
    datatype: string
    data: any
  }) {
    super()
    this.uuid = uuid
    this.component = 'island'
    this.name = 'island'
    this.model = model
    this.part = part
    this.variant = variant
    this.section = section
    this._swatch = indexed_swatches[data.material_index]
    this._designable = data['designable']
    this._quilt = new QuiltElement(this)
    this._perforation = new Perforation(this)

    if (datatype !== 'island') {
      console.error(
        `There is an issues with the data provided` +
          `@model:${model.uuid}` +
          `@part:${part.uuid}` +
          `@variant${variant.uuid}` +
          `@section:${section.uuid}` +
          `@island:${uuid}`
      )
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setIndex(data.v)
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        data.p.map((e: any) => e * model.precision_factor),
        3
      )
    )
    geometry.setAttribute(
      'normal',
      new THREE.Float32BufferAttribute(
        data.n.map((e: any) => e * model.precision_factor),
        3
      )
    )
    geometry.setAttribute(
      'uv',
      new THREE.Float32BufferAttribute(
        data.uv.map((e: any) => e * model.precision_factor),
        2
      )
    )
    geometry.computeTangents()

    const colors = []
    for (let i = 0; i < data.ao.length; i++) {
      const clr = data.ao[i]
      colors.push(clr, clr, clr)
    }
    geometry.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(
        colors.map((e: any) => e * model.precision_factor * 0.6 + 0.4),
        3
      )
    )
    geometry.setAttribute(
      'ao',
      new THREE.Float32BufferAttribute(
        data.ao.map((e: any) => e * model.precision_factor),
        1
      )
    )
    const material = new Material({ island: this })
    this._material = material
    const mesh = new THREE.Mesh(geometry, material)

    this.add(mesh)
  }

  // SETTERS
  ////////////////////////////////////////////////////////////////////////////////

  public set swatch(e: number) {
    this._swatch = SLib.getSwatch(e)
  }

  // GETTERS
  ////////////////////////////////////////////////////////////////////////////////

  public get designable() {
    return this._designable
  }

  public get material(): Material {
    return this._material
  }

  public get swatchI(): Swatch {
    return this._swatch
  }

  public get perforation(): Perforation {
    return this._perforation
  }

  public get quilt(): Quilt | undefined {
    return this._quilt.quilt
  }

  public get quiltData() {
    return this._quilt.enabled && this._quilt.quilt
      ? this._quilt.quilt.data
      : undefined
  }

  public get cnvColor() {
    if (!this._canvasColor) {
      this._canvasColor = this.generateCanvas()
      this._textureColor = new THREE.CanvasTexture(this._canvasColor)
      this._textureColor.rotation = Math.PI / 2 // NOTE: UVs seem to be rotated?
      this._textureColor.repeat.set(1, 1)
      this._textureColor.wrapS = THREE.RepeatWrapping
      this._textureColor.wrapT = THREE.RepeatWrapping
      // tex.minFilter = THREE.NearestFilter
      // tex.magFilter = THREE.NearestFilter
      document.body.appendChild(this._canvasColor)
    }
    const ctnx = this._canvasColor.getContext('2d')
    if (ctnx)
      this._canvasColor.width = this._canvasColor.height = this.resolution
    if (ctnx && this.perforation.enabled && !this.quilt) {
      const bounds = this._canvasColor.width
      const bC = bounds / 2
      const distance = this.perforation.distance
      const size =
        ((this.perforation.size / distance) * this.perforation.radius) / 4
      const center = size / 2
      ctnx.fillStyle = this.swatchI.hex
      ctnx.fillRect(0, 0, bounds, bounds)
      for (let ii = -1; ii <= 1; ii = ii + 2) {
        for (let jj = -1; jj <= 1; jj = jj + 2) {
          ctnx.drawImage(
            this.perforation.texture,
            bC + ii * bC - center,
            bC + jj * bC - center,
            size,
            size
          )
          ctnx.drawImage(
            this.perforation.texture2,
            bC * ii,
            bC * jj,
            bounds,
            bounds
          )
        }
      }
    }
    if (ctnx && this.perforation.enabled && this.quilt) {
      const bounds = this._canvasColor.width
      const bC = bounds / 2
      const distance = this.perforation.distance
      const size =
        ((this.perforation.size / distance) * this.perforation.radius) / 4
      const center = size / 2
      ctnx.fillStyle = this.swatchI.hex
      ctnx.fillRect(0, 0, bounds, bounds)
      for (let ii = -1; ii <= 1; ii = ii + 2) {
        for (let jj = -1; jj <= 1; jj = jj + 2) {
          ctnx.drawImage(
            this.perforation.texture,
            bC + ii * bC - center,
            bC + jj * bC - center,
            size,
            size
          )
          ctnx.drawImage(
            this.perforation.texture2,
            bC * ii,
            bC * jj,
            bounds,
            bounds
          )
        }
      }
    }
    if (ctnx && this.quilt) {
      const resolution = this.resolution
      ctnx.fillStyle = this.swatchI.hex
      ctnx.fillRect(0, 0, resolution, resolution)
      ctnx.drawImage(this.quilt.colorMap, 0, 0, resolution, resolution)
    }
    return this._canvasColor
  }

  public get cnvBump() {
    if (!this._canvasBump) {
      this._canvasBump = this.generateCanvas()
      this._textureBump = new THREE.CanvasTexture(this._canvasBump)
      this._textureBump.rotation = Math.PI / 2 // NOTE: UVs seem to be rotated?
      this._textureBump.repeat.set(1, 1)
      this._textureBump.wrapS = THREE.RepeatWrapping
      this._textureBump.wrapT = THREE.RepeatWrapping
      // tex.minFilter = THREE.NearestFilter
      // tex.magFilter = THREE.NearestFilter
      document.body.appendChild(this._canvasBump)
    }
    const ctnx = this._canvasBump.getContext('2d')
    if (ctnx) this._canvasBump.width = this._canvasBump.height = this.resolution
    if (ctnx && this.perforation.enabled && !this.quilt) {
      const bounds = this._canvasBump.width
      const bC = bounds / 2
      const distance = this.perforation.distance
      const size =
        ((this.perforation.size / distance) * this.perforation.radius) / 4
      const center = size / 2
      ctnx.fillStyle = 'white'
      ctnx.fillRect(0, 0, bounds, bounds)
      for (let ii = -1; ii <= 1; ii = ii + 2) {
        for (let jj = -1; jj <= 1; jj = jj + 2) {
          ctnx.drawImage(
            this.perforation.texture,
            bC + ii * bC - center,
            bC + jj * bC - center,
            size,
            size
          )
          const overlap = 3
          const offset = (bounds * overlap - bounds) / 2
          const x = -offset - (bounds / 2) * ii
          const y = -offset - (bounds / 2) * jj
          ctnx.drawImage(
            this.perforation.texture2,
            x,
            y,
            bounds * overlap,
            bounds * overlap
          )
        }
      }
    }
    if (ctnx && this.quilt) {
      const resolution = this.resolution
      this._canvasBump.width = this._canvasBump.height = resolution
      ctnx.fillStyle = 'white' // Hardcoding white for bumpmap
      ctnx.fillRect(0, 0, resolution, resolution)
      ctnx.drawImage(this.quilt.bumpMap, 0, 0, resolution, resolution)
    }
    return this._canvasBump
  }

  public get texColor() {
    return this._textureColor
  }

  public get texBump() {
    return this._textureBump
  }

  public applyQuilt(q: Quilt | undefined) {
    this._quilt.quilt = q
  }

  public refresh() {
    if (this._debounce_refresh) clearTimeout(this._debounce_refresh)
    this._debounce_refresh = setTimeout(() => {
      const stage = Stage.getInstance()
      if (this.quilt && this.cnvColor && this.cnvBump) {
        const [x, y] = this.quilt.scale
        const r = this._quilt.rotation
        this.material.color = new THREE.Color('white')
        this.material.map = this.texColor!
        this.material.bumpMap = this.texBump!
        this.material.bumpScale = 1
        this.material.normalMap = null
        this.material.map.repeat.set(1 / (x * 2), 1 / (y * 2))
        this.material.bumpMap.repeat.set(1 / x, 1 / y)
        this.material.map.rotation = this.material.bumpMap.rotation =
          r * (Math.PI / 180)
        this.material.map.needsUpdate = true
        this.material.bumpMap.needsUpdate = true
        this.material.needsUpdate = true
      } else if (this.perforation.enabled && this.cnvColor && this.cnvBump) {
        const x: number = this.perforation.distance / 10
        const y: number = this.perforation.distance / 10
        const r = 0
        this.material.color = new THREE.Color('white')
        this.material.map = this.texColor!
        this.material.bumpMap = this.texBump!
        this.material.bumpScale = 1
        this.material.normalMap = null
        this.material.map.repeat.set(1 / x, 1 / y)
        this.material.bumpMap.repeat.set(1 / x, 1 / y)
        this.material.map.rotation = this.material.bumpMap.rotation =
          r * (Math.PI / 180)
        this.material.map.needsUpdate = true
        this.material.bumpMap.needsUpdate = true
        this.material.needsUpdate = true
      } else {
        this.material.color = this.swatchI.color
        this.material.map = null
        this.material.bumpMap = null
        this.material.normalMap = stage.defLeatherNormals
        this.material.normalMap!.needsUpdate = true
        this.material.needsUpdate = true
      }
      stage.update()
    }, 100)
  }
}

export default Island
